/**
 * LocationsMapPanel — the shared Locations map surface, built to match the
 * evaluation Sites tab: a vertically scrolling column of location cards on the
 * left, an embedded MapLibre surface on the right carrying lettered markers,
 * the legend, and the Layers panel (markers / trade-area rings / overlaps /
 * demographics).
 *
 *   ┌────────┬────────────────────────────────────────┐
 *   │ Card A │ Legend                    warnings ▸   │
 *   │ Card B │ Layers                                 │
 *   │ Card C │            ⒶⒷⒸ  markers + rings        │
 *   │   ⇕    │                                        │
 *   └────────┴────────────────────────────────────────┘
 *
 * With `showCards={false}` the card column drops out and the map takes the full
 * width; `showLayers={false}` drops the Layers box too. The legend still lists
 * every location and its rows zoom to one.
 *
 * The lettering is positional and comes from the same helper `SiteMarkers` and
 * `MapLegend` use, so card, legend row and pin always agree.
 *
 * Used by the Locations index (full-width), the portfolio's Locations tab and
 * the project's Map tab. They differ only in what a card/marker click does,
 * whether a saved view is restored, and whether the cards are shown — so those
 * are props rather than forks.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import type maplibregl from 'maplibre-gl'
import { AlertCircle, Building2 } from 'lucide-react'
import { Card, CardContent } from 'components/ui/card'
import { cn } from 'lib/utils'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import {
	MapContainer,
	SiteMarkers,
	MapLegend,
	MapLayersPanel,
	TradeAreaRings,
	OverlapZones,
	DemographicHeatmap,
	OtherRetailersLayer,
	useMapLayerState,
	letterForIndex,
	tradeAreaService,
	type SiteForRings,
	type RingVisibilityConfig,
	type SiteForOverlap,
} from 'features/Map'
import { toMappableSites, toPreciseSites } from 'screens/evaluations/utils/siteMapPosition'
import OtherRetailPanel, { type OtherRetailItem } from './OtherRetailPanel'
import OtherRetailScanDialog from './OtherRetailScanDialog'
import useOtherRetailers from 'features/OtherRetailers/useOtherRetailers'
import useIsMobile from 'hooks/useIsMobile'

export interface MapViewState {
	lat: number
	lng: number
	zoom: number
}

export interface LocationsMapPanelProps {
	locations: any[]
	/** Fired by a card click and by a marker click. */
	onSelectLocation: (location: any) => void
	/**
	 * Fired by the marker popup's "Goto Location" button. Leave it out and that
	 * button is not drawn, so the popup keeps "View Details" alone.
	 */
	onGotoLocation?: (location: any) => void
	/** Heading on the legend card. */
	legendTitle?: string
	/**
	 * Show the left-hand card column. Turn it off for a full-width map — the
	 * legend still lists every location, and its rows zoom to one.
	 */
	showCards?: boolean
	/**
	 * Show the Layers box. Turn it off for a plain marker map — the layer state
	 * keeps its defaults, so markers stay on and rings, overlaps and the
	 * demographic heatmap stay off.
	 */
	showLayers?: boolean
	/**
	 * A previously captured view to restore instead of auto-fitting — used when
	 * returning from a location's detail page so the map is where it was left.
	 */
	initialView?: MapViewState | null
	/**
	 * Show the "Other Retail" container under the location cards: nearby retail
	 * that is not in our system (POIs, competitors, proximate cotenants).
	 * Off by default, so the index and portfolio map views are unchanged.
	 */
	showOtherRetail?: boolean
	/** Rows for that container. Read-only context, never Location records. */
	otherRetail?: OtherRetailItem[]
	otherRetailLoading?: boolean
	onSelectOtherRetail?: (item: OtherRetailItem) => void
	/**
	 * The asset saved retailers hang off.
	 *
	 * Passed explicitly rather than derived from `locations[0]`, because this
	 * panel is also used for a centre plus its tenants and for whole-portfolio
	 * views, where the first location in the list is not the subject.
	 *
	 * The Projects map resolves its own `projectAsset` here, which is a
	 * Location, so the same asset shows one shared list on both screens.
	 */
	anchorType?: 'Location'
	anchorId?: string
}

export interface LocationsMapPanelHandle {
	getViewState: () => MapViewState | null
}

/**
 * Per-location avatar: the location's image when it has one, otherwise the
 * lettered badge matching its marker and legend row. Tracks its own load error
 * so a dead image URL degrades to the letter without affecting siblings.
 */
function LocationAvatar({ imageUrl, letter }: { imageUrl?: string | null; letter: string }) {
	const [errored, setErrored] = useState(false)
	const showImage = imageUrl && !errored
	return (
		<div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
			{showImage ? (
				<img src={imageUrl} alt="" loading="lazy" onError={() => setErrored(true)} className="w-10 h-10 object-cover" />
			) : (
				<div className="w-10 h-10 flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] font-semibold text-sm">
					{letter}
				</div>
			)}
		</div>
	)
}

const LocationsMapPanel = forwardRef<LocationsMapPanelHandle, LocationsMapPanelProps>(
	(
		{
			locations,
			onSelectLocation,
			onGotoLocation,
			legendTitle = 'Locations',
			showCards = true,
			showLayers = true,
			initialView = null,
			showOtherRetail = false,
			otherRetail = [],
			otherRetailLoading = false,
			onSelectOtherRetail,
			anchorType,
			anchorId,
		},
		ref
	) => {
		const isMobile = useIsMobile()
		const [map, setMap] = useState<maplibregl.Map | null>(null)
		const [isMapLoaded, setIsMapLoaded] = useState(false)
		const [popupLocation, setPopupLocation] = useState<any>(null)

		// Per-radius rings model, matching the Sites tab: nothing is drawn until
		// the user picks at least one radius. Shared with EvaluationSites since
		// MYD-486, where this block existed verbatim.
		const {
			showMarkers,
			toggleMarkers,
			enabledRadii,
			toggleRadius,
			showOverlaps,
			toggleOverlaps,
			showDemographics,
			toggleDemographics,
		} = useMapLayerState()

		const [showOtherRetailers, setShowOtherRetailers] = useState(true)

		/**
		 * Rings narrow, they do not gate. An empty selection means no narrowing,
		 * so everything saved shows. Computed once here and handed to the hook,
		 * so the sidebar list and the map layer read one array and can never
		 * disagree about what is within a mile.
		 */
		const ringLimit = useMemo(
			() => tradeAreaService.outermostRadius(enabledRadii),
			[enabledRadii]
		)

		const retailers = useOtherRetailers({
			anchorType,
			anchorId,
			ringLimit,
			enabled: showOtherRetail,
		})

		/** Names the subject in the scan dialog's subtitle, when we have it. */
		const anchorName = useMemo(
			() => locations.find((l: any) => String(l?._id) === String(anchorId))?.assetName ?? null,
			[locations, anchorId]
		)

		/**
		 * Saved rows carry the API's shape; the panel takes `OtherRetailItem`.
		 * Mapping here rather than widening the panel's contract keeps it a
		 * presentational component that any caller can feed.
		 */
		const retailerItems: OtherRetailItem[] = useMemo(
			() =>
				retailers.visibleItems.map((r: any) => ({
					id: r._id,
					name: r.name,
					category: r.category,
					brand: r.brandName,
					address: r.address,
					city: r.city,
					stateProvince: r.stateProvince,
					postalCode: r.postalCode,
					latitude: r.latitude,
					longitude: r.longitude,
					distanceMiles: r.distanceMiles,
					image: r.logoUrl ?? null,
					monogram: r.monogram,
					monogramColor: r.monogramColor,
					source: r.source,
				})),
			[retailers.visibleItems]
		)

		useImperativeHandle(
			ref,
			() => ({
				getViewState: () => {
					if (!map) return null
					const center = map.getCenter()
					return { lat: center.lat, lng: center.lng, zoom: map.getZoom() }
				},
			}),
			[map]
		)

		// Locations carry no approximate fallback coordinates, so every mappable
		// location is also a precise one. Reusing the shared resolver anyway keeps
		// this and the evaluation map on identical placement rules if that changes.
		const mappableLocations = useMemo(() => toMappableSites(locations), [locations])
		const preciseLocations = useMemo(() => toPreciseSites(locations), [locations])
		const unplaceableCount = locations.length - mappableLocations.length

		const ringSites: SiteForRings[] = useMemo(
			() =>
				preciseLocations.map((l: any) => ({
					id: l._id,
					latitude: l.latitude,
					longitude: l.longitude,
				})),
			[preciseLocations]
		)
		const overlapSites: SiteForOverlap[] = ringSites

		const ringVisibility: RingVisibilityConfig = useMemo(() => {
			const out: RingVisibilityConfig = {}
			for (const l of preciseLocations) out[l._id as string] = enabledRadii
			return out
		}, [preciseLocations, enabledRadii])

		// A restored view is a position the user deliberately left the map at, so
		// it wins over the auto-fit and is applied only once.
		const didRestoreViewRef = useRef(false)

		// The auto-fit keys off what is actually on the map rather than off the
		// identity of the array carrying it: callers rebuild that array on every
		// render, so fitting on the array itself snapped the map back out to the
		// full extent whenever anything else on the screen changed - opening the
		// edit slider from a marker, for one.
		const fitSignature = useMemo(
			() => mappableLocations.map((l: any) => `${l._id}:${l.longitude},${l.latitude}`).join('|'),
			[mappableLocations]
		)
		const lastFitSignatureRef = useRef<string | null>(null)

		useEffect(() => {
			if (!map || !isMapLoaded) return

			if (initialView && !didRestoreViewRef.current) {
				didRestoreViewRef.current = true
				map.setCenter([initialView.lng, initialView.lat])
				map.setZoom(initialView.zoom)
				return
			}
			if (didRestoreViewRef.current) return
			if (mappableLocations.length === 0) return
			if (lastFitSignatureRef.current === fitSignature) return
			lastFitSignatureRef.current = fitSignature

			// Fit to the full extent of the outermost ring rather than to the
			// points, so turning rings on never pushes them off-screen.
			const bounds = tradeAreaService.getBoundsForSitesWithRings(
				mappableLocations.map((l: any) => ({ longitude: l.longitude, latitude: l.latitude }))
			)
			map.fitBounds(bounds as maplibregl.LngLatBoundsLike, {
				padding: 40,
				duration: 600,
				maxZoom: 13,
			})
		}, [map, isMapLoaded, mappableLocations, fitSignature, initialView])

		const handleMarkerClick = useCallback(
			(locationId: string) => {
				const match = locations.find(l => l && String(l._id) === String(locationId))
				if (match) onSelectLocation(match)
			},
			[locations, onSelectLocation]
		)

		const handleMarkerGoto = useCallback(
			(locationId: string) => {
				if (!onGotoLocation) return
				const match = locations.find(l => l && String(l._id) === String(locationId))
				if (match) onGotoLocation(match)
			},
			[locations, onGotoLocation]
		)

		return (
			<div
				className={cn(
					/*
					 * svh, not vh, on a phone.
					 *
					 * iOS Safari reports 100vh as the FULL screen height and ignores
					 * its own chrome, so a vh-sized map runs below the visible area
					 * and anything pinned to its bottom — the basemap switcher — ends
					 * up behind the toolbar. Measured on an iPhone 14: the switcher
					 * sat at 747px against roughly 664px of visible content.
					 *
					 * svh is the SMALL viewport height, the viewport with the chrome
					 * showing, so the bottom stays reachable whether or not the
					 * toolbars are hidden. dvh would also fit but resizes the map
					 * while the user scrolls.
					 *
					 * min-h is the fallback: a browser that does not know the unit
					 * drops the declaration, and these children are absolutely
					 * positioned, so without a minimum the map would collapse.
					 *
					 * An emulator cannot catch this — Playwright's viewport has no
					 * browser chrome, so vh and the visible area are the same number.
					 */
					isMobile
						? 'h-[calc(100svh-300px)] min-h-[320px]'
						: 'h-[calc(100vh-280px)] min-h-[500px]',
					showCards && 'grid grid-cols-6 gap-4',
				)}
			>
				{/* Left column — location cards in a vertical scroller (1/6) */}
				{showCards && (
					<div className="col-span-1 h-full min-h-0 overflow-y-auto overscroll-contain space-y-2 pr-1">
						{locations.map((location: any, index: number) => {
							const letter = letterForIndex(index)
							const hasCoords = location.latitude != null && location.longitude != null
							// "Austin, TX 78701" — comma between city and state, space before
							// the postal code, and each part dropped cleanly when absent.
							const cityStateZip = [
								[location.city, location.stateProvince].filter(Boolean).join(', '),
								location.postalCode,
							]
								.filter(Boolean)
								.join(' ')

							return (
								<Card
									key={location._id}
									className="overflow-hidden cursor-pointer hover:shadow-sm transition-shadow"
									onClick={() => onSelectLocation(location)}
								>
									<CardContent className="p-4">
										{/* Image and identity share the top row; the address block below
									    runs the full width of the card so it needs fewer wraps. */}
										<div className="flex items-start gap-3">
											<LocationAvatar imageUrl={location.image} letter={letter} />
											<div className="min-w-0 flex-1">
												<h4 className="font-semibold text-sm text-gray-900 truncate" title={location.assetName}>
													{location.assetName}
												</h4>
												{location.locationType && (
													<p className="text-xs text-gray-500 truncate" title={location.locationType}>
														{location.locationType}
													</p>
												)}
											</div>
										</div>

										{(location.address || cityStateZip) && (
											<button
												type="button"
												disabled={!hasCoords}
												onClick={e => {
													// The card itself runs onSelectLocation.
													e.stopPropagation()
													if (hasCoords) setPopupLocation(location)
												}}
												title={hasCoords ? 'Open map' : 'No coordinates available'}
												className={cn(
													'mt-2 block w-full text-left text-xs transition-colors',
													hasCoords
														? 'text-gray-600 hover:text-[#0080fe] cursor-pointer'
														: 'text-gray-400 cursor-not-allowed'
												)}
											>
												{location.address && (
													<span className="block truncate" title={location.address}>
														{location.address}
													</span>
												)}
												{cityStateZip && (
													<span className="block truncate text-gray-400" title={cityStateZip}>
														{cityStateZip}
													</span>
												)}
											</button>
										)}
									</CardContent>
								</Card>
							)
						})}

						{/* Nearby retail that is not in our system: POIs, competitors and
						    proximate cotenants. Read-only context beneath the real locations. */}
						{showOtherRetail && (
							<OtherRetailPanel
								items={retailers.isAvailable ? retailerItems : otherRetail}
								isLoading={retailers.isAvailable ? retailers.isLoading : otherRetailLoading}
								onSelectItem={onSelectOtherRetail}
								onScan={retailers.isAvailable ? retailers.openScan : undefined}
								onRemoveItem={retailers.isAvailable ? retailers.removeItem : undefined}
								ringLimit={retailers.items.length > 0 ? ringLimit : null}
							/>
						)}
					</div>
				)}

				{/* Map — 5/6 beside the cards, or the full width without them */}
				<div
					className={cn(
						'relative rounded-lg overflow-hidden border border-gray-200',
						showCards ? 'col-span-5' : 'h-full'
					)}
				>
					<MapContainer
						onMapLoad={m => {
							setMap(m)
							setIsMapLoaded(true)
						}}
					>
						<TradeAreaRings
							map={map}
							isMapLoaded={isMapLoaded}
							sites={ringSites}
							visibility={ringVisibility}
							visible={enabledRadii.length > 0}
						/>
						<OverlapZones
							map={map}
							isMapLoaded={isMapLoaded}
							sites={overlapSites}
							radii={enabledRadii}
							visible={showOverlaps && enabledRadii.length > 0}
						/>
						{showMarkers && (
							<SiteMarkers
								map={map}
								sites={mappableLocations}
								onMarkerClick={handleMarkerClick}
								onGotoLocation={onGotoLocation ? handleMarkerGoto : undefined}
							/>
						)}
						<DemographicHeatmap map={map} isMapLoaded={isMapLoaded} visible={showDemographics} />
						{/* After the overlays and before the site markers, so the
						    user's own assets always draw on top of context. */}
						<OtherRetailersLayer
							map={map}
							isMapLoaded={isMapLoaded}
							retailers={retailers.visibleItems}
							visible={showOtherRetailers && retailers.isAvailable}
						/>
					</MapContainer>

					<div className="absolute top-4 left-4 z-20 flex flex-col gap-3 max-w-[240px]">
						{/* The legend is a 240px overlay — most of the width of a 390px
						    map, and on a phone the list beneath the map already says
						    what it says. */}
						{!isMobile && mappableLocations.length > 0 && (
							<MapLegend
								sites={mappableLocations}
								title={legendTitle}
								map={map}
								className="max-h-[50vh] overflow-y-auto"
							/>
						)}
						{showLayers && (
							<MapLayersPanel
								showMarkers={showMarkers}
								onToggleMarkers={toggleMarkers}
								enabledRadii={enabledRadii}
								onToggleRadius={toggleRadius}
								showOverlaps={showOverlaps}
								onToggleOverlaps={toggleOverlaps}
								showDemographics={showDemographics}
								onToggleDemographics={toggleDemographics}
								showOtherRetailers={showOtherRetailers}
								onToggleOtherRetailers={
									retailers.isAvailable ? () => setShowOtherRetailers(v => !v) : undefined
								}
								otherRetailersLabel={
									retailers.isAvailable && retailers.items.length > 0
										? `${retailers.visibleItems.length}${ringLimit != null ? ` · ${ringLimit} mi` : ''}`
										: undefined
								}
							/>
						)}
					</div>

					{unplaceableCount > 0 && (
						<div className="absolute top-4 right-4 z-20 flex flex-col items-end gap-1">
							<div className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50/95 backdrop-blur border border-gray-200 px-2 py-1 rounded shadow-sm">
								<AlertCircle className="w-3 h-3" />
								{unplaceableCount} location{unplaceableCount === 1 ? '' : 's'} hidden — no coordinates to place{' '}
								{unplaceableCount === 1 ? 'it' : 'them'} by
							</div>
						</div>
					)}

					{mappableLocations.length === 0 && (
						<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
							<div className="pointer-events-auto rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm px-4 py-3 max-w-sm text-center">
								<Building2 className="mx-auto h-8 w-8 text-gray-300 mb-2" />
								<p className="text-sm text-gray-500">
									No locations placed yet — add an address to see them on the map.
								</p>
							</div>
						</div>
					)}
				</div>

				{popupLocation && (
					<MapLocationPopupCN
						open={!!popupLocation}
						onClose={() => setPopupLocation(null)}
						location={{
							latitude: popupLocation.latitude,
							longitude: popupLocation.longitude,
							assetName: popupLocation.assetName,
							address: popupLocation.address,
							city: popupLocation.city,
							stateProvince: popupLocation.stateProvince,
							image: popupLocation.image,
							retailer: popupLocation.retailer,
						}}
					/>
				)}

				{retailers.isScanOpen && (
					<OtherRetailScanDialog
						open={retailers.isScanOpen}
						onClose={retailers.closeScan}
						anchorName={anchorName}
						onScan={retailers.runScan}
						onSave={retailers.saveSelection}
					/>
				)}
			</div>
		)
	}
)

LocationsMapPanel.displayName = 'LocationsMapPanel'

export default LocationsMapPanel
